import { CbhIcon, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { type ReactNode } from "react";

import { IconButton } from "../components/IconButton";

const BACK_BUTTON_WIDTH = 40;

interface ChatHeaderProps {
  title: string;
  subtitle?: string;
  onBack: () => void;
  onTitleClick?: () => void;
  titleVariant?: "h6" | "body1" | "body2" | "caption";
  showTitleChevron?: boolean;
  children?: ReactNode;
}

export function ChatHeader(props: ChatHeaderProps) {
  const {
    title,
    subtitle,
    onBack,
    onTitleClick,
    titleVariant = "h6",
    showTitleChevron,
    children,
  } = props;

  const shouldShowChevron = showTitleChevron ?? Boolean(onTitleClick);
  const isTitleClickable = Boolean(onTitleClick);

  return (
    <Stack spacing={3} sx={{ paddingBottom: 4, paddingX: 5, width: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <IconButton iconType="chevron-left" variant="outlined" onClick={onBack} />

        <Stack
          spacing={1}
          paddingX={1}
          minWidth={0}
          alignItems="center"
          sx={{
            flex: 1,
            cursor: isTitleClickable ? "pointer" : "default",
          }}
          onClick={onTitleClick}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: "100%" }}>
            <Text
              variant={titleVariant}
              sx={(theme) => ({
                color: theme.palette.text.primary,
                fontWeight: 500,
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              })}
            >
              {title}
            </Text>

            {shouldShowChevron && <CbhIcon type="chevron-right" size="xSmall" />}
          </Stack>

          {subtitle && (
            <Text
              variant="caption"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                fontWeight: 500,
                textAlign: "center",
              })}
            >
              {subtitle}
            </Text>
          )}
        </Stack>

        <Box sx={{ width: BACK_BUTTON_WIDTH }} />
      </Stack>

      {children}
    </Stack>
  );
}
